import React, { memo, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Services from '../../api/service';
import SectionTitle from '../SectionTitle';
import OptimizedImage from '../OptimizedImage';
import { ConfigurableContainer } from '../ConfigurableElement';
import { useUIConfig } from '../../hooks/useUIConfig';
import arabicBg from '../../images/arabic-bg-dark.svg';
import boyQuestionImg from '../../images/children/boy_with_question_mark.png';
import girlBlushingImg from '../../images/children/girl_blushing.png';
import './style.css';
import { useTranslation } from 'react-i18next';

const ServiceSection = memo((props) => {
    const { applyCSS } = useUIConfig();
    const { t, i18n } = useTranslation();
    const isRTL = i18n.language === 'ar';

    // Helper to fix number/slash sequences in RTL
    const processRTLText = (text) => {
        if (!isRTL || !text) return text;
        // Wrap patterns like "24/7" with LTR span
        return text.replace(/(\d+\/\d+)/g, '<span dir="ltr" style="unicode-bidi: embed;">$1</span>');
    };

    // Apply CSS when component mounts
    useEffect(() => {
        applyCSS();
    }, [applyCSS]);

    const handleClick = useCallback(() => {
        window.scrollTo(10, 0);
    }, []);

    // Map service IDs to translation keys
    const serviceKeys = {
        '1': { title: 'services.access247', desc: 'services.access247Desc' },
        '2': { title: 'services.testsQuizzes', desc: 'services.testsQuizzesDesc' },
        '3': { title: 'services.enjoyableExperience', desc: 'services.enjoyableExperienceDesc' },
        '4': { title: 'services.interactiveLearning', desc: 'services.interactiveLearningDesc' },
        '5': { title: 'services.innovativeApproach', desc: 'services.innovativeApproachDesc' },
        '6': { title: 'services.qualifiedTeachers', desc: 'services.qualifiedTeachersDesc' }
    };

    const renderServices = Services.map((service, index) => {
        const keys = serviceKeys[service.Id] || { title: '', desc: '' };
        return (
            <div className="col-lg-4 col-md-6 col-12" key={service.Id}>
                <div className="wpo-service-item">
                    <div className="wpo-service-icon">
                        <OptimizedImage
                            src={service.icon}
                            alt={t(keys.title)}
                            loading="lazy"
                            decoding="async"
                            width={64}
                            height={64}
                        />
                    </div>
                    <div className="wpo-service-text">
                        <h2>
                            <Link onClick={handleClick} to={`/service-single/${service.Id}`}>
                                <span dangerouslySetInnerHTML={{ __html: processRTLText(t(keys.title)) }} />
                            </Link>
                        </h2>
                        <p>{t(keys.desc)}</p>
                    </div>
                    <div className="visible-icon">
                        <OptimizedImage
                            src={service.icon2}
                            alt={t(keys.title)}
                            loading="lazy"
                            decoding="async"
                            width={64}
                            height={64}
                        />
                    </div>
                </div>
            </div>
        );
    });

    return (
        <section
            className={`wpo-service-section ${props.sClass}`}
            style={{
                position: 'relative'
            }}
        >
            {/* Arabic Background */}
            <div
                style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundImage: `url(${arabicBg})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                    opacity: 0.05, // Increased from 0.02 to 0.05
                    zIndex: 0
                }}
            />
            <div className="container" style={{ position: 'relative', zIndex: 1 }}>
                <SectionTitle subTitle={t('services.uniqueExperience')} MainTitle={t('services.anasLearningPlatform')} vTitle={''} />
                <div className="row">
                    {renderServices}
                </div>
            </div>

            {/* Boy with question mark positioned on the left */}
            <ConfigurableContainer
                section="services"
                element="boyQuestion"
                className="services-boy-question-container"
            >
                <img src={boyQuestionImg} alt="Boy with question mark" className="services-boy-question-img" loading="lazy" decoding="async" />
            </ConfigurableContainer>

            {/* Girl blushing positioned on the right */}
            <ConfigurableContainer
                section="services"
                element="girlBlushing"
                className="services-girl-blushing-container"
            >
                <img src={girlBlushingImg} alt="Girl blushing" className="services-girl-blushing-img" loading="lazy" decoding="async" />
            </ConfigurableContainer>
        </section>
    )
});

export default ServiceSection;